import React, { useMemo, useRef, useCallback } from 'react'
import { OverlayTrigger } from 'react-bootstrap'
import type { OverlayTriggerProps } from 'react-bootstrap/OverlayTrigger'
import { generateId } from '@ztechapps/common.utils'
import { CloseButton } from '../CloseButton'
import { HorizontalStack } from '../HorizontalStack'

const DEFAULT_DELAY = { hide: 0, show: 500 }

export type TooltipPopOverProps = Omit<OverlayTriggerProps, 'children' | 'overlay' | 'delay'> & {
  children: React.ReactNode
  tooltipBody?: React.ReactNode
  tooltipHeader?: React.ReactNode
  overlay?: OverlayTriggerProps['overlay']
  trigger?: OverlayTriggerProps['trigger']
  width?: string | number
  /**
   * Milliseconds before showing/hiding the popover. Mirrors Bootstrap `OverlayTrigger.delay` object form.
   * Partial overrides merge over the default `{ hide: 0, show: 500 }` — e.g. `{ show: 1000 }` keeps `hide: 0`.
   * `show` applies to hover triggers; focus opens immediately per Bootstrap behavior.
   */
  delay?: { show?: number; hide?: number }
}
export const TooltipPopOver = ({
  children,
  tooltipBody,
  tooltipHeader,
  onToggle,
  showHeader,
  overlay,
  width,
  delay,
  ...props
}: TooltipPopOverProps & { showHeader?: boolean }) => {
  const idRef = React.useRef(generateId(5))
  const [show, setShow] = React.useState(false)
  const isOverPopover = useRef(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const handlePopoverMouseEnter = useCallback(() => {
    isOverPopover.current = true
    clearHideTimeout()
  }, [clearHideTimeout])

  const handlePopoverMouseLeave = useCallback(() => {
    isOverPopover.current = false
    setShow(false)
  }, [])

  //automatically setup the close button if there is showHeader set as true
  let header = tooltipHeader
  if (showHeader) {
    header = (
      <HorizontalStack className="justify-content-between">
        <div>{tooltipHeader}</div>
        <CloseButton onClick={() => setShow(false)} />
      </HorizontalStack>
    )
  }

  const hasHeader = !!header
  const popoverContent = (
    <div id={`popover${idRef.current}`} className="popover shadow-lg" style={{ display: 'block', position: 'relative' }}>
      {hasHeader && <div className="popover-header">{header}</div>}
      <div className="popover-body">
        <div style={{ width: width ?? undefined }}>{tooltipBody}</div>
      </div>
    </div>
  )

  const popover = overlay ?? (
    <div
      onMouseEnter={handlePopoverMouseEnter}
      onMouseLeave={handlePopoverMouseLeave}
      style={{ position: 'relative', zIndex: 1100 }}
    >
      {popoverContent}
    </div>
  )

  const trigger = useMemo<OverlayTriggerProps['trigger']>(() => {
    if (showHeader) return ['click']
    return ['hover', 'focus']
  }, [showHeader])

  const otherProps = useMemo<any>(() => {
    if (show && showHeader)
      return {
        rootClose: true,
        onHide: () => {
          setShow(false)
        },
      }
    return {
      rootClose: false,
      onHide: null,
    }
  }, [show])

  const resolvedDelay = useMemo(() => ({ ...DEFAULT_DELAY, ...(delay ?? {}) }), [delay])

  const handleToggle = useCallback(
    (nextShow: boolean) => {
      if (nextShow) {
        clearHideTimeout()
        setShow(true)
        onToggle?.(true)
      } else {
        // Delay hide to allow mouse to move to popover
        hideTimeoutRef.current = setTimeout(() => {
          if (!isOverPopover.current) {
            setShow(false)
            onToggle?.(false)
          }
        }, 100)
      }
    },
    [onToggle, clearHideTimeout]
  )

  return (
    <OverlayTrigger
      placement="auto"
      delay={resolvedDelay}
      trigger={trigger}
      show={show}
      onToggle={handleToggle}
      {...{ ...props, ...otherProps }}
      overlay={popover}
    >
      <span>{children}</span>
    </OverlayTrigger>
  )
}
